import { useState, useEffect, useCallback } from 'react';

/**
 * CDC-TMA T31: per-user preferences stored server-side (/api/user/prefs),
 * isolated to the current account. Module-level cache so all components share
 * one fetch; write-through on set with cross-component sync.
 */
let _cache = null;
let _promise = null;
const _listeners = new Set();

function loadPrefs() {
  if (_cache) return Promise.resolve(_cache);
  if (!_promise) {
    _promise = fetch('/api/user/prefs', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { prefs: {} }))
      .then((d) => { _cache = d.prefs || {}; return _cache; })
      .catch(() => { _cache = {}; return _cache; });
  }
  return _promise;
}

function notify() {
  _listeners.forEach((l) => l());
}

/**
 * @param {string} key   preference key
 * @param {*} defaultValue value when unset
 * @returns {[*, (v:*) => Promise<void>]} [value, setValue]
 */
export function useUserPrefs(key, defaultValue) {
  const [value, setValue] = useState(() => (_cache ? (_cache[key] ?? defaultValue) : defaultValue));

  useEffect(() => {
    let active = true;
    const sync = () => { if (active) setValue(_cache ? (_cache[key] ?? defaultValue) : defaultValue); };
    loadPrefs().then(sync);
    _listeners.add(sync);
    return () => { active = false; _listeners.delete(sync); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const set = useCallback(async (newVal) => {
    _cache = { ...(_cache || {}), [key]: newVal };
    notify();
    try {
      await fetch('/api/user/prefs', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value: newVal }),
      });
    } catch (e) {
      // Keep the optimistic value; the next load will reconcile.
      console.error('Failed to save preference', key, e);
    }
  }, [key]);

  return [value, set];
}

export default useUserPrefs;
